import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, errText } from "../lib/api";
import { Mic, Play, Loader2, RefreshCcw } from "lucide-react";

/**
 * A single voice option with Play + Regenerate buttons. Clicking the label selects
 * the voice; Play fetches a cached 5-second TTS clip and plays it inline;
 * Regenerate forces a fresh clip (bypasses cache) — useful if a voice sounds off.
 * Only one sample plays at a time.
 */
const currentAudio = { ref: { current: null } };  // module-level singleton

export function VoiceOption({ voiceKey, label, selected, onSelect }) {
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const stopCurrent = () => {
    if (currentAudio.ref.current && currentAudio.ref.current !== audioRef.current) {
      try { currentAudio.ref.current.pause(); } catch (_) {}
    }
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) {}
    }
    setPlaying(false);
  };

  const playFrom = async (force = false) => {
    stopCurrent();
    if (force) setRegenerating(true); else setLoading(true);
    try {
      const { data } = await api.get(`/companions/${voiceKey}/voice-sample${force ? "?force=true" : ""}`);
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const audio = new Audio(`${base}/api${data.url}`);
      audioRef.current = audio;
      currentAudio.ref.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => { setPlaying(false); toast.error("Playback failed"); };
      await audio.play();
      setPlaying(true);
      if (force) toast.success(`Fresh sample generated for ${label}`);
    } catch (e) {
      toast.error(errText(e) || "Voice sample failed");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  const onPlayClick = async (e) => {
    e.stopPropagation();
    if (playing && audioRef.current) { stopCurrent(); return; }
    playFrom(false);
  };
  const onRegenerateClick = async (e) => {
    e.stopPropagation();
    if (regenerating) return;
    playFrom(true);
  };

  return (
    <div className={`inline-flex items-stretch rounded-md overflow-hidden border ${selected ? "border-primary" : "border-input"}`}
         data-testid={`voice-option-${voiceKey}`}>
      <Button type="button" size="sm" variant={selected ? "default" : "ghost"}
              className="rounded-none border-0"
              onClick={onSelect} data-testid={`video-voice-${voiceKey}-btn`}>
        <Mic size={13} className="mr-1" /> {label}
      </Button>
      <button type="button" onClick={onPlayClick} disabled={loading || regenerating}
              className="px-2 flex items-center justify-center hover:bg-muted transition-colors border-l"
              data-testid={`voice-preview-${voiceKey}-btn`} title={`Play a 5-second sample of ${label}`}>
        {loading ? <Loader2 size={12} className="animate-spin" /> :
         playing ? <span className="text-[10px] font-bold text-accent">■</span> :
         <Play size={12} />}
      </button>
      <button type="button" onClick={onRegenerateClick} disabled={regenerating || loading}
              className="px-2 flex items-center justify-center hover:bg-muted transition-colors border-l text-muted-foreground hover:text-foreground"
              data-testid={`voice-regenerate-${voiceKey}-btn`}
              title={`Regenerate a fresh sample for ${label} (forces new TTS)`}>
        {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={11} />}
      </button>
    </div>
  );
}
